import { ticketService, type ActivityLogEntry, type ActivityLogDetail, type ActivityEventType } from '@/services/ticket-service';
import { navigate } from '@/utils/navigate';
import './nu-user-menu';

function sevenDaysAgo(): Date {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d;
}

function toDateInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

const EVENT_LABELS: Record<ActivityEventType, string> = {
  CHECK_IN:   'Check In',
  CHECK_OUT:  'Check Out',
  SMS_SENT:   'SMS Sent',
  SMS_FAILED: 'SMS Failed',
};

const EVENT_CLASSES: Record<ActivityEventType, string> = {
  CHECK_IN:   'badge-neutral',
  CHECK_OUT:  'badge-kiln',
  SMS_SENT:   'badge-on',
  SMS_FAILED: 'badge-err',
};

class NuActivityLogs extends HTMLElement {
  private readonly root: ShadowRoot;
  private _logs: ActivityLogEntry[] = [];
  private _loading = false;
  private _error = '';

  // filter state
  private _filterEventType = '';
  private _filterFrom = toDateInput(sevenDaysAgo());
  private _filterTo = toDateInput(new Date());

  // inline expanded rows: logId → detail | 'loading' | 'error'
  private _expanded = new Map<string, ActivityLogDetail | 'loading' | 'error'>();

  // detail modal state (for non-SMS_FAILED rows)
  private _detail: ActivityLogDetail | null = null;
  private _detailLoading = false;
  private _detailError = '';

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() { return ['org-id']; }
  get orgId() { return this.getAttribute('org-id') ?? ''; }

  connectedCallback() {
    this._load();
  }

  private async _load() {
    this._loading = true;
    this._error = '';
    this._render();

    try {
      const params: Record<string, string> = {};
      if (this._filterEventType) params.eventType = this._filterEventType;
      if (this._filterFrom)      params.from = new Date(this._filterFrom).toISOString();
      if (this._filterTo) {
        const d = new Date(this._filterTo);
        d.setHours(23, 59, 59, 999);
        params.to = d.toISOString();
      }
      this._logs = await ticketService.getActivityLogs(this.orgId, params);
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Failed to load activity logs.';
    }

    this._loading = false;
    this._render();
  }

  private async _toggleExpand(logId: string) {
    if (this._expanded.has(logId)) {
      this._expanded.delete(logId);
      this._refreshExpanded(logId);
      return;
    }
    this._expanded.set(logId, 'loading');
    this._refreshExpanded(logId);
    try {
      const detail = await ticketService.getActivityLog(this.orgId, logId);
      this._expanded.set(logId, detail);
    } catch {
      this._expanded.set(logId, 'error');
    }
    this._refreshExpanded(logId);
  }

  private _refreshExpanded(logId: string) {
    const expandRow = this.root.querySelector<HTMLElement>(`[data-expand-id="${logId}"]`);
    if (!expandRow) return;
    const state = this._expanded.get(logId);
    if (!state) {
      expandRow.style.display = 'none';
      const btn = this.root.querySelector<HTMLElement>(`[data-toggle-id="${logId}"]`);
      if (btn) btn.textContent = 'show error';
      return;
    }
    expandRow.style.display = '';
    const btn = this.root.querySelector<HTMLElement>(`[data-toggle-id="${logId}"]`);
    if (btn) btn.textContent = 'hide';
    const cell = expandRow.querySelector<HTMLElement>('.expand-content');
    if (!cell) return;
    if (state === 'loading') {
      cell.innerHTML = `<span class="spinner-sm spinner-dark"></span> Loading…`;
    } else if (state === 'error') {
      cell.innerHTML = `<span class="expand-err">Failed to load detail.</span>`;
    } else {
      cell.innerHTML = `
        ${state.phoneNum ? `<span class="expand-kv"><span class="expand-key">Phone</span><span class="expand-val mono">${state.phoneNum}</span></span>` : ''}
        <span class="expand-kv"><span class="expand-key">Error</span><span class="expand-val expand-err mono">${state.errorMsg ?? '—'}</span></span>
      `;
    }
  }

  private async _openDetail(logId: string) {
    this._detail = null;
    this._detailLoading = true;
    this._detailError = '';
    this._renderModal();

    try {
      this._detail = await ticketService.getActivityLog(this.orgId, logId);
    } catch (err) {
      this._detailError = err instanceof Error ? err.message : 'Failed to load details.';
    }

    this._detailLoading = false;
    this._renderModal();
  }

  private _closeDetail() {
    this._detail = null;
    this._detailLoading = false;
    this._detailError = '';
    const modal = this.root.querySelector<HTMLElement>('#detail-modal');
    if (modal) modal.style.display = 'none';
  }

  private _summary(): Record<ActivityEventType, number> {
    const counts: Record<ActivityEventType, number> = { CHECK_IN: 0, CHECK_OUT: 0, SMS_SENT: 0, SMS_FAILED: 0 };
    for (const log of this._logs) {
      if (log.eventType in counts) counts[log.eventType as ActivityEventType]++;
    }
    return counts;
  }

  private _summaryHTML(): string {
    const counts = this._summary();
    return (Object.keys(EVENT_LABELS) as ActivityEventType[])
      .filter(k => counts[k] > 0)
      .map(k => `<span class="summary-pill ${EVENT_CLASSES[k]}">${EVENT_LABELS[k]} <span class="summary-count">${counts[k]}</span></span>`)
      .join('');
  }

  private _rowsHTML(): string {
    if (!this._logs.length) {
      return `<tr><td class="empty-cell" colspan="3">No activity found.</td></tr>`;
    }
    return this._logs.map(log => {
      const ts = new Date(log.createdTS);
      const dateStr = ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = ts.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      const cls = EVENT_CLASSES[log.eventType as ActivityEventType] ?? 'badge-neutral';
      const label = EVENT_LABELS[log.eventType as ActivityEventType] ?? log.eventType;
      const isFailed = log.eventType === 'SMS_FAILED';
      const isExpanded = this._expanded.has(log.logId);

      return `
        <tr class="log-row ${isFailed ? '' : 'log-row--clickable'}" data-log-id="${log.logId}" ${!isFailed ? 'role="button" tabindex="0"' : ''}>
          <td class="col-ts">
            <span class="ts-date">${dateStr}</span>
            <span class="ts-time mono">${timeStr}</span>
          </td>
          <td>
            <span class="event-badge ${cls}">${label}</span>
            ${isFailed ? `<button class="show-more-btn" data-toggle-id="${log.logId}">${isExpanded ? 'hide' : 'show error'}</button>` : ''}
          </td>
          <td class="col-ticket mono">${log.ticketNum}</td>
        </tr>
        ${isFailed ? `
        <tr class="expand-row" data-expand-id="${log.logId}" style="${isExpanded ? '' : 'display:none'}">
          <td colspan="3" class="expand-cell">
            <div class="expand-content"></div>
          </td>
        </tr>` : ''}`;
    }).join('');
  }

  private _modalBodyHTML(): string {
    if (this._detailLoading) {
      return `<div class="modal-loading"><span class="spinner-sm spinner-dark"></span> Loading…</div>`;
    }
    if (this._detailError) {
      return `<p class="modal-error">${this._detailError}</p>`;
    }
    if (!this._detail) return '';

    const d = this._detail;
    const cls = EVENT_CLASSES[d.eventType as ActivityEventType] ?? 'badge-neutral';
    const label = EVENT_LABELS[d.eventType as ActivityEventType] ?? d.eventType;
    const ts = new Date(d.createdTS).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });

    return `
      <div class="detail-grid">
        <span class="detail-label mono">Event</span>
        <span class="event-badge ${cls}">${label}</span>

        <span class="detail-label mono">Ticket</span>
        <span class="detail-val mono">${d.ticketNum}</span>

        <span class="detail-label mono">Time</span>
        <span class="detail-val mono">${ts}</span>

        <span class="detail-label mono">Phone</span>
        <span class="detail-val mono">${d.phoneNum ?? '—'}</span>

        ${d.errorMsg ? `
        <span class="detail-label mono">Error</span>
        <span class="detail-val detail-err mono">${d.errorMsg}</span>
        ` : ''}

        <span class="detail-label mono">Log ID</span>
        <span class="detail-val mono detail-id">${d.logId}</span>
      </div>
    `;
  }

  private _renderModal() {
    const modal = this.root.querySelector<HTMLElement>('#detail-modal');
    if (!modal) return;
    modal.style.display = 'flex';
    const body = modal.querySelector<HTMLElement>('#modal-body');
    if (body) body.innerHTML = this._modalBodyHTML();
  }

  private _render() {
    this.root.innerHTML = this._styles() + `
      <div class="page">
        <header class="top-bar">
          <span class="brand"><span class="dot"></span>Activity Logs</span>
          <div class="header-actions">
            <button class="btn-back" id="back-btn">← Back</button>
            <nu-user-menu></nu-user-menu>
          </div>
        </header>

        <div class="body">
          <div class="filters">
            <div class="filter-group">
              <label class="filter-label mono" for="filter-event">Event</label>
              <select class="filter-select" id="filter-event">
                <option value="">All</option>
                <option value="CHECK_IN"   ${this._filterEventType === 'CHECK_IN'   ? 'selected' : ''}>Check In</option>
                <option value="CHECK_OUT"  ${this._filterEventType === 'CHECK_OUT'  ? 'selected' : ''}>Check Out</option>
                <option value="SMS_SENT"   ${this._filterEventType === 'SMS_SENT'   ? 'selected' : ''}>SMS Sent</option>
                <option value="SMS_FAILED" ${this._filterEventType === 'SMS_FAILED' ? 'selected' : ''}>SMS Failed</option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label mono" for="filter-from">From</label>
              <input class="filter-input" id="filter-from" type="date" value="${this._filterFrom}">
            </div>
            <div class="filter-group">
              <label class="filter-label mono" for="filter-to">To</label>
              <input class="filter-input" id="filter-to" type="date" value="${this._filterTo}">
            </div>
            <button class="btn-apply" id="apply-btn" ${this._loading ? 'disabled' : ''}>
              ${this._loading ? '<span class="spinner-sm"></span>' : 'Apply'}
            </button>
          </div>

          ${this._error ? `<p class="error-msg">${this._error}</p>` : ''}

          ${!this._loading && !this._error && this._logs.length > 0 ? `
            <div class="summary-row">${this._summaryHTML()}</div>
          ` : ''}

          <div class="table-wrap">
            <table class="log-table">
              <thead>
                <tr>
                  <th class="col-ts">Time</th>
                  <th>Event</th>
                  <th class="col-ticket">Ticket</th>
                </tr>
              </thead>
              <tbody>
                ${this._loading
                  ? `<tr><td class="empty-cell" colspan="3"><span class="spinner-sm spinner-dark"></span> Loading…</td></tr>`
                  : this._rowsHTML()}
              </tbody>
            </table>
          </div>

          ${!this._loading && this._logs.length === 100 ? `
            <p class="limit-note mono">Showing first 100 results — use filters to narrow the range.</p>
          ` : ''}
        </div>
      </div>

      <div id="detail-modal" class="modal-backdrop" style="display:none" role="dialog" aria-modal="true">
        <div class="modal">
          <div class="modal-header">
            <span class="modal-title">Log Detail</span>
            <button class="modal-close" id="modal-close" aria-label="Close">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                <line x1="2" y1="2" x2="12" y2="12"/><line x1="12" y1="2" x2="2" y2="12"/>
              </svg>
            </button>
          </div>
          <div class="modal-body" id="modal-body"></div>
        </div>
      </div>
    `;

    this.root.querySelector('#back-btn')!.addEventListener('click', () => navigate(`/app/org/${this.orgId}`));

    this.root.querySelector('#apply-btn')!.addEventListener('click', () => {
      this._filterEventType = (this.root.querySelector<HTMLSelectElement>('#filter-event')!).value;
      this._filterFrom = (this.root.querySelector<HTMLInputElement>('#filter-from')!).value;
      this._filterTo = (this.root.querySelector<HTMLInputElement>('#filter-to')!).value;
      this._load();
    });

    this.root.querySelector('#modal-close')!.addEventListener('click', () => this._closeDetail());

    this.root.querySelector('#detail-modal')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this._closeDetail();
    });

    this.root.querySelector('tbody')!.addEventListener('click', (e) => {
      const toggle = (e.target as HTMLElement).closest<HTMLElement>('[data-toggle-id]');
      if (toggle?.dataset.toggleId) {
        e.stopPropagation();
        this._toggleExpand(toggle.dataset.toggleId);
        return;
      }
      const row = (e.target as HTMLElement).closest<HTMLElement>('.log-row--clickable');
      if (row?.dataset.logId) this._openDetail(row.dataset.logId);
    });

    this.root.querySelector('tbody')!.addEventListener('keydown', (e) => {
      const row = (e.target as HTMLElement).closest<HTMLElement>('.log-row--clickable');
      if (row?.dataset.logId && (e as KeyboardEvent).key === 'Enter') {
        this._openDetail(row.dataset.logId);
      }
    });
  }

  private _styles(): string {
    return `<style>
      :host { display: block; }
      *, *::before, *::after { box-sizing: border-box; }

      .page {
        min-height: 100vh;
        background: var(--paper-light, #FAF7F0);
        color: var(--ink, #2A2622);
        font-family: 'Inter', sans-serif;
      }
      .mono { font-family: 'IBM Plex Mono', monospace; }

      .top-bar {
        display: flex; align-items: center; justify-content: space-between;
        padding: 12px 20px;
        border-bottom: 1px solid var(--line, #C9C0AC);
        background: rgba(250,247,240,0.95);
        backdrop-filter: blur(6px);
        position: sticky; top: 0; z-index: 10;
      }
      .brand {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 18px; letter-spacing: -0.02em;
        display: flex; align-items: center; gap: 7px;
      }
      .dot {
        width: 8px; height: 8px; border-radius: 50%;
        background: var(--kiln, #C97A2E);
        display: inline-block; flex-shrink: 0;
      }

      .body {
        max-width: 860px; margin: 0 auto;
        padding: 24px 20px 48px;
        display: flex; flex-direction: column; gap: 20px;
      }

      /* filters */
      .filters {
        display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
        padding: 16px;
        background: var(--paper, #E4DED2);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 6px;
      }
      .filter-group { display: flex; flex-direction: column; gap: 5px; }
      .filter-label {
        font-size: 10px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-soft, #5A5346);
      }
      .filter-select, .filter-input {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 4px; padding: 6px 10px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink, #2A2622); cursor: pointer;
      }
      .filter-input { font-family: 'IBM Plex Mono', monospace; }
      .filter-select:focus, .filter-input:focus {
        outline: 2px solid var(--kiln, #C97A2E); outline-offset: 1px; border-color: transparent;
      }
      .btn-apply {
        display: flex; align-items: center; gap: 6px;
        background: var(--kiln, #C97A2E); color: var(--paper-light, #FAF7F0);
        border: none; border-radius: 4px; padding: 7px 18px;
        font-family: 'Inter', sans-serif; font-weight: 600; font-size: 13px;
        cursor: pointer; transition: background .15s; align-self: flex-end;
      }
      .btn-apply:hover:not(:disabled) { background: var(--kiln-dark, #A15F1F); }
      .btn-apply:disabled { opacity: 0.6; cursor: not-allowed; }

      /* summary pills */
      .summary-row { display: flex; gap: 8px; flex-wrap: wrap; }
      .summary-pill {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 4px 12px; border-radius: 12px;
        font-size: 12px; font-weight: 600;
      }
      .summary-count {
        display: inline-flex; align-items: center; justify-content: center;
        min-width: 18px; height: 18px;
        background: rgba(0,0,0,.1); border-radius: 9px; padding: 0 4px;
        font-size: 11px;
      }

      /* table */
      .table-wrap { border: 1px solid var(--line, #C9C0AC); border-radius: 6px; overflow: hidden; }
      .log-table { width: 100%; border-collapse: collapse; font-size: 13px; }
      .log-table thead tr {
        background: var(--paper, #E4DED2);
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .log-table th {
        padding: 10px 14px; text-align: left;
        font-family: 'IBM Plex Mono', monospace;
        font-size: 10px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-soft, #5A5346); font-weight: 600;
      }
      .log-table tbody tr {
        background: var(--paper-light, #FAF7F0);
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .log-table tbody tr:last-child { border-bottom: none; }
      .log-row--clickable { cursor: pointer; transition: background .1s; }
      .log-row--clickable:hover, .log-row--clickable:focus { background: var(--paper, #E4DED2); outline: none; }

      .show-more-btn {
        display: inline-block; margin-left: 8px;
        background: none; border: none; padding: 0;
        font-family: 'IBM Plex Mono', monospace; font-size: 11px;
        color: #c0392b; cursor: pointer; text-decoration: underline;
        text-underline-offset: 2px;
      }
      .show-more-btn:hover { opacity: 0.75; }

      .expand-row { background: rgba(192,57,43,.04); }
      .expand-cell { padding: 10px 14px 12px 14px !important; border-bottom: 1px solid var(--line, #C9C0AC); }
      .expand-content { display: flex; flex-direction: column; gap: 6px; }
      .expand-kv { display: flex; gap: 12px; align-items: baseline; }
      .expand-key {
        font-family: 'IBM Plex Mono', monospace; font-size: 10px;
        letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-soft, #5A5346); min-width: 48px; flex-shrink: 0;
      }
      .expand-val { font-size: 12px; color: var(--ink, #2A2622); word-break: break-all; }
      .expand-err { color: #c0392b; }
      .log-table td { padding: 10px 14px; vertical-align: middle; }

      .col-ts { width: 180px; }
      .col-ticket { width: 90px; }
      .ts-date { display: block; color: var(--ink, #2A2622); font-size: 13px; }
      .ts-time { display: block; font-size: 11px; color: var(--ink-soft, #5A5346); }

      .event-badge {
        display: inline-block; padding: 2px 9px;
        border-radius: 10px; font-size: 12px; font-weight: 600; white-space: nowrap;
      }
      .badge-neutral { background: rgba(90,83,70,.1);    color: var(--ink-soft, #5A5346); }
      .badge-kiln    { background: rgba(201,122,46,.12); color: var(--kiln-dark, #A15F1F); }
      .badge-on      { background: rgba(110,127,92,.15); color: var(--glaze, #6E7F5C); }
      .badge-err     { background: rgba(192,57,43,.12);  color: #c0392b; }

      .empty-cell {
        text-align: center; padding: 40px 14px;
        color: var(--ink-soft, #5A5346);
        font-family: 'IBM Plex Mono', monospace; font-size: 13px;
      }
      .error-msg { font-size: 13px; color: #c0392b; font-family: 'IBM Plex Mono', monospace; margin: 0; }
      .limit-note { font-size: 11px; color: var(--ink-soft, #5A5346); margin: 0; text-align: center; }

      /* modal */
      .modal-backdrop {
        position: fixed; inset: 0; z-index: 50;
        background: rgba(42,38,34,.45);
        backdrop-filter: blur(2px);
        display: flex; align-items: center; justify-content: center;
        padding: 20px;
      }
      .modal {
        background: var(--paper-light, #FAF7F0);
        border: 1px solid var(--line, #C9C0AC);
        border-radius: 8px;
        width: 100%; max-width: 420px;
        box-shadow: 0 8px 32px rgba(42,38,34,.2);
        overflow: hidden;
      }
      .modal-header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 18px;
        background: var(--paper, #E4DED2);
        border-bottom: 1px solid var(--line, #C9C0AC);
      }
      .modal-title {
        font-family: 'Space Grotesk', sans-serif;
        font-weight: 700; font-size: 15px; letter-spacing: -0.01em;
      }
      .modal-close {
        display: flex; align-items: center; justify-content: center;
        width: 26px; height: 26px;
        background: transparent; border: 1px solid transparent;
        border-radius: 4px; color: var(--ink-soft, #5A5346);
        cursor: pointer; padding: 0; transition: border-color .12s, background .12s;
      }
      .modal-close:hover { border-color: var(--line, #C9C0AC); background: var(--paper-light, #FAF7F0); }
      .modal-body { padding: 20px 18px; }

      .detail-grid {
        display: grid; grid-template-columns: 80px 1fr;
        gap: 12px 16px; align-items: baseline;
      }
      .detail-label {
        font-size: 10px; letter-spacing: .08em; text-transform: uppercase;
        color: var(--ink-soft, #5A5346); padding-top: 2px;
      }
      .detail-val { font-size: 13px; color: var(--ink, #2A2622); word-break: break-all; }
      .detail-err { color: #c0392b; }
      .detail-id { font-size: 11px; color: var(--ink-soft, #5A5346); }

      .modal-loading {
        display: flex; align-items: center; gap: 8px;
        font-size: 13px; color: var(--ink-soft, #5A5346);
        font-family: 'IBM Plex Mono', monospace;
      }
      .modal-error { font-size: 13px; color: #c0392b; margin: 0; font-family: 'IBM Plex Mono', monospace; }

      .spinner-sm {
        display: inline-block; width: 14px; height: 14px;
        border: 2px solid rgba(250,247,240,.4);
        border-top-color: var(--paper-light, #FAF7F0);
        border-radius: 50%; animation: spin .7s linear infinite; vertical-align: middle;
      }
      .spinner-dark {
        border-color: var(--line, #C9C0AC);
        border-top-color: var(--kiln, #C97A2E);
      }
      @keyframes spin { to { transform: rotate(360deg); } }

      .header-actions { display: flex; align-items: center; gap: 10px; }
      .btn-back {
        background: transparent; border: 1px solid var(--line, #C9C0AC);
        border-radius: 5px; padding: 5px 12px;
        font-family: 'Inter', sans-serif; font-size: 13px;
        color: var(--ink-soft, #5A5346); cursor: pointer; transition: background .15s;
      }
      .btn-back:hover { background: var(--paper-light, #FAF7F0); }
    </style>`;
  }
}

customElements.define('nu-activity-logs', NuActivityLogs);